import React from "react";
import { Link } from "react-router-dom";
import {
  FiSmartphone,
  FiMail,
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiGlobe,
  FiCalendar,
  FiArrowRight,
  FiMapPin,
  FiClock,
  FiHeadphones,
} from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Top Footer Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-6">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1 md:mr-4">
              <h2 className="text-lg md:text-xl font-bold text-white">
                {t("readyToRevolutionize")}
              </h2>
              <p className="text-white text-opacity-90 text-xs md:text-sm">
                {t("joinRestaurants")}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a
                href="https://calendar.app.google/GCLQcvn6cB3NWfap9"
                target="_blank"
                rel="noreferrer"
                className="min-w-fit bg-white text-orange-600 hover:bg-gray-100 font-medium text-xs md:text-sm px-3 py-2 rounded-md flex items-center whitespace-nowrap"
              >
                <FiCalendar className="mr-1 flex-shrink-0" size={14} />
                <span className="truncate">{t("bookAppointment")}</span>
              </a>
              <Link
                to="/contact"
                className="min-w-fit bg-gray-900 text-white hover:bg-gray-800 font-medium text-xs md:text-sm px-3 py-2 rounded-md flex items-center whitespace-nowrap"
              >
                <FiHeadphones className="mr-1 flex-shrink-0" size={14} />
                <span className="truncate">{t("contactUs")}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Brand & Social Column */}
          <div className="md:col-span-4 space-y-4">
            <div>
              <h3 className="text-xl font-bold mb-1">
                <span className="text-orange-500">Byte</span>Belly
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                By <span className="font-semibold text-gray-300">Solylock</span>
              </p>
              <p className="text-sm text-gray-300 mb-4">
                {t("footerCompanyDescription")}
              </p>
            </div>

            <div className="flex space-x-1">
              <a
                href="https://solylock.dev"
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-md text-gray-300 hover:text-white transition-colors"
              >
                <FiGlobe size={16} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-md text-gray-300 hover:text-white transition-colors"
              >
                <FiInstagram size={16} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-md text-gray-300 hover:text-white transition-colors"
              >
                <FiFacebook size={16} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-md text-gray-300 hover:text-white transition-colors"
              >
                <FiTwitter size={16} />
              </a>
            </div>
          </div>

          {/* Links & Features */}
          <div className="md:col-span-2">
            <h4 className="text-base font-semibold mb-3 text-gray-100">
              {t("quickLinks")}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center"
                >
                  <FiArrowRight
                    className="mr-1.5 text-orange-500 opacity-70"
                    size={12}
                  />
                  {t("home")}
                </Link>
              </li>
              <li>
                <Link
                  to="/features"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center"
                >
                  <FiArrowRight
                    className="mr-1.5 text-orange-500 opacity-70"
                    size={12}
                  />
                  {t("features")}
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center"
                >
                  <FiArrowRight
                    className="mr-1.5 text-orange-500 opacity-70"
                    size={12}
                  />
                  {t("pricing")}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center"
                >
                  <FiArrowRight
                    className="mr-1.5 text-orange-500 opacity-70"
                    size={12}
                  />
                  {t("contact")}
                </Link>
              </li>
              <li>
                <a
                  href="https://foodapp.solylock.dev"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center"
                >
                  <FiArrowRight
                    className="mr-1.5 text-orange-500 opacity-70"
                    size={12}
                  />
                  {t("demoLink")}
                </a>
              </li>
            </ul>
          </div>

          {/* Technologies Column */}
          <div className="md:col-span-2">
            <h4 className="text-base font-semibold mb-3 text-gray-100">
              {t("poweredBy")}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <svg
                    className="w-3.5 h-3.5 text-orange-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                  </svg>
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">React Native & Flutter</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <svg
                    className="w-3.5 h-3.5 text-orange-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                  </svg>
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">Firebase & Websocket</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <svg
                    className="w-3.5 h-3.5 text-orange-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"></path>
                  </svg>
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">{t("cloudServices")}</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-3 border-t border-gray-800 pt-3">
                {t("trustedByRestaurants")}
              </p>
            </div>
          </div>

          {/* Contact & Booking Column */}
          <div className="md:col-span-4">
            <h4 className="text-base font-semibold mb-3 text-gray-100">
              {t("contactUsFooter")}
            </h4>

            <div className="space-y-2 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <FiSmartphone className="text-orange-500" size={14} />
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">+90 553 331 98 34</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <FiMail className="text-orange-500" size={14} />
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">info@solylock.com</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="flex-shrink-0 w-7 h-7 bg-gray-800 rounded-md flex items-center justify-center mr-2">
                  <FiMapPin className="text-orange-500" size={14} />
                </div>
                <div className="text-gray-400">
                  <p className="text-sm">{t("officeLocation")}</p>
                </div>
              </div>
            </div>

            {/* Book Appointment Card */}
            <div className="bg-gray-800 rounded-lg p-3 mt-4">
              <div className="flex items-center mb-2">
                <FiCalendar className="text-orange-500 mr-2" size={16} />
                <h5 className="text-white font-medium text-sm">
                  {t("bookAppointment")}
                </h5>
              </div>
              <div className="flex gap-2 mb-2">
                <div className="flex-1 bg-gray-700 p-1.5 rounded text-center">
                  <span className="text-orange-400 text-xs font-medium">
                    {t("videoConsultation")}
                  </span>
                  <div className="text-gray-400 text-xs">{t("minutes30")}</div>
                </div>
                <div className="flex-1 bg-gray-700 p-1.5 rounded text-center">
                  <span className="text-orange-400 text-xs font-medium">
                    {t("demoSession")}
                  </span>
                  <div className="text-gray-400 text-xs">{t("minutes45")}</div>
                </div>
              </div>
              <a
                href="https://calendar.app.google/GCLQcvn6cB3NWfap9"
                target="_blank"
                rel="noreferrer"
                className="w-full py-1.5 px-3 bg-orange-500 hover:bg-orange-600 text-white rounded flex items-center justify-center text-sm font-medium transition-colors"
              >
                <FiClock className="mr-1.5" size={14} />
                {t("scheduleNow")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-4 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} Solylock. {t("allRightsReserved")}
          </p>
          <div className="flex space-x-6 mt-2 md:mt-0">
            <a href="#" className="text-gray-500 hover:text-gray-300 text-xs">
              {t("termsOfService")}
            </a>
            <a href="#" className="text-gray-500 hover:text-gray-300 text-xs">
              {t("privacyPolicy")}
            </a>
            <a href="#" className="text-gray-500 hover:text-gray-300 text-xs">
              {t("cookiePolicy")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
